import React, { useContext } from 'react';
import { filterOperatorOptions, RelationsProps } from '@fabric-ui/components';
import { TokenSuggestion } from './components/TokenFilterInput/types';

export interface ExpressionBuilder {
  fields?: any[];
  isLoadingFields?: boolean;
  defaultField?: string | null;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  operatorOptions?: typeof filterOperatorOptions;
  relationsProps?: RelationsProps;
  disableMessage?: string;
  isOverExpressionsLimit?: boolean;
  fieldSelectionMessage?: string;
  fieldOnly?: boolean;
  hasListTab?: boolean;
  isHistorical?: boolean;
  shouldShowIcon?: (operand: any) => boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
  fieldExtraOptions?: Record<string, string[]>;
  timeRange?: Date[];
  fieldsListAdornment?: React.ReactNode;
  fieldDescription?: string;
  icon?: React.ReactNode;
  canDeleteLast?: boolean;
  readOnly?: boolean;
  tokenSuggestions?: TokenSuggestion[];
  ingressModeProps?: { showToggle: boolean; multiSelect: boolean; isIconCollapsed?: boolean };
}

export const defaultExpressionBuilder: ExpressionBuilder = {};

export const expressionBuilderKeys: (keyof ExpressionBuilder)[] = [
  'fields',
  'isLoadingFields',
  'defaultField',
  'canAddNewFields',
  'canSelectFieldType',
  'supportParser',
  'operatorOptions',
  'relationsProps',
  'disableMessage',
  'isOverExpressionsLimit',
  'fieldSelectionMessage',
  'fieldOnly',
  'hasListTab',
  'isHistorical',
  'shouldShowIcon',
  'defaultMode',
  'excludeNullFromList',
  'fieldExtraOptions',
  'timeRange',
  'fieldsListAdornment',
  'fieldDescription',
  'icon',
  'canDeleteLast',
  'readOnly',
  'tokenSuggestions',
  'ingressModeProps',
];

export const pickExpressionBuilder = <T extends object>(props: T): [ExpressionBuilder, Omit<T, keyof ExpressionBuilder>] => {
  const expressionBuilder: any = {};
  const rest: any = {};
  (Object.keys(props) as Array<keyof T>).forEach(key => {
    const stringKey = key as string;
    if (expressionBuilderKeys.includes(stringKey as any)) {
      const value = props[key];
      if (value !== undefined) expressionBuilder[stringKey] = value;
    } else {
      rest[stringKey] = props[key];
    }
  });
  return [expressionBuilder as ExpressionBuilder, rest as Omit<T, keyof ExpressionBuilder>];
};

const ExpressionBuilderContext = React.createContext<ExpressionBuilder>(defaultExpressionBuilder);

export const ExpressionBuilderProvider: React.FC<{ options?: ExpressionBuilder; children: React.ReactNode }> = ({
  options = defaultExpressionBuilder,
  children,
}) => <ExpressionBuilderContext.Provider value={options}>{children}</ExpressionBuilderContext.Provider>;

export const useExpressionBuilder = () => useContext(ExpressionBuilderContext);
